import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { ArrowLeft, KeyRound, Mail as MailIcon, ShieldCheck, Smartphone } from 'lucide-react';
import type { TwoFactorMethod } from '@welynk/types';
import {
  useLogin,
  useResendSignupOtp,
  useRequestTwoFactorOtp,
  useCompleteTwoFactorLogin,
} from '../api';
import { useAuthStore } from '../store';
import { AuthApiService } from '@/lib/services';
import { CareerProfileApiService } from '@/lib/services/career-profile-api';
import { storeTokens } from '@/lib/token-manager';
import { useToast } from '@/hooks/useToast';
import { AuthShell, FloatField, OtpDigitInput } from '../AuthShared';
import { SsoButtons } from '../components/SsoButtons';
import { AccountApiService } from '@/lib/services/account-api';
import { apiErrorDetails, apiErrorMessage } from '@/lib/api-error-utils';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

// SVG icons for Google / LinkedIn social buttons
interface TwoFactorChallenge {
  tempToken: string;
  availableMethods: TwoFactorMethod[];
}

interface MethodCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  /** Visually disabled but still clickable (e.g. "coming soon" toast). */
  muted?: boolean;
  busy?: boolean;
  badge?: string;
}

const MethodCard: React.FC<MethodCardProps> = ({
  icon,
  title,
  description,
  onClick,
  disabled,
  muted,
  busy,
  badge,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || busy}
    aria-disabled={disabled || muted || undefined}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      width: '100%',
      textAlign: 'left',
      padding: '14px 16px',
      borderRadius: 12,
      border: '1.5px solid var(--line)',
      background: '#fff',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled || muted ? 0.55 : 1,
      transition: 'border-color .15s, box-shadow .15s, opacity .15s',
      fontFamily: 'inherit',
    }}
    onMouseEnter={(e) => {
      if (!disabled && !muted) e.currentTarget.style.borderColor = 'var(--teal)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = 'var(--line)';
    }}
  >
    <span
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: 'var(--teal-2)',
        color: 'var(--teal-3)',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      {icon}
    </span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--ink)',
        }}
      >
        {busy ? 'Sending…' : title}
        {badge && (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 99,
              background: 'rgba(232,155,74,0.12)',
              border: '1px solid rgba(232,155,74,0.25)',
              color: 'var(--warn)',
            }}
          >
            {badge}
          </span>
        )}
      </span>
      <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
        {description}
      </span>
    </span>
  </button>
);

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const setUser = useAuthStore((state) => state.setUser);
  const loginMutation = useLogin();
  const resendOtpMutation = useResendSignupOtp();
  const requestTwoFactorOtpMutation = useRequestTwoFactorOtp();
  const completeTwoFactorMutation = useCompleteTwoFactorLogin();

  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get('ssoError');
  const collegeSlug = searchParams.get('college');
  // Signed in with the right password to an archived / deletion-pending account.
  const [deactivated, setDeactivated] = useState<{
    email: string;
    password: string;
    rememberMe: boolean;
    deletionScheduledFor: string | null;
  } | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  // 'method' stage: pick how to receive/enter the code; 'code' stage: enter it.
  const [stage, setStage] = useState<'method' | 'code'>('method');
  const [method, setMethod] = useState<TwoFactorMethod>('EMAIL_OTP');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Tracks the last navigation-state message already toasted, so React 18
  // StrictMode's dev-only double-invoke of this effect (and any re-render
  // with the same location.state) doesn't show the same toast twice.
  const shownMessageRef = useRef<string | null>(null);
  useEffect(() => {
    const state = location.state as { message?: string };
    if (state?.message && shownMessageRef.current !== state.message) {
      shownMessageRef.current = state.message;
      toast.warning(state.message);
    }
  }, [location, toast]);

  // Entering 2FA mode always starts at method selection with clean state.
  useEffect(() => {
    if (!challenge) return;
    setStage('method');
    setMethod('EMAIL_OTP');
    setUseBackupCode(false);
    setBackupCode('');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError(null);
    setResendCooldown(0);
  }, [challenge]);

  // Resend cooldown countdown.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const resetCodeEntry = () => {
    setOtpDigits(['', '', '', '', '', '']);
    setBackupCode('');
    setOtpError(null);
  };

  const totpEnabled = challenge?.availableMethods.includes('TOTP') ?? false;

  // Email code is only generated when the user actually picks the email
  // method — avoids burning codes/rate-limit for authenticator users.
  const selectEmailMethod = () => {
    if (!challenge) return;
    requestTwoFactorOtpMutation.mutate(challenge.tempToken, {
      onSuccess: () => {
        setMethod('EMAIL_OTP');
        setUseBackupCode(false);
        resetCodeEntry();
        setStage('code');
        setResendCooldown(60);
      },
      onError: (err: any) => {
        const message =
          err?.response?.data?.error?.message || err?.message || 'Could not send the code.';
        toast.error(message);
      },
    });
  };

  const selectTotpMethod = () => {
    if (!totpEnabled) return;
    setMethod('TOTP');
    setUseBackupCode(false);
    resetCodeEntry();
    setStage('code');
  };

  const selectBackupCode = () => {
    setUseBackupCode(true);
    resetCodeEntry();
    setStage('code');
  };

  const afterLoginSuccess = async (
    accessToken: string,
    expiresIn: number,
    refreshExpiresIn?: number
  ) => {
    // The refresh token arrives as an httpOnly cookie; only the access token is stored.
    storeTokens(accessToken, expiresIn, refreshExpiresIn);
    try {
      const userData = await AuthApiService.getCurrentUser();
      setUser(userData);
    } catch {
      // Profile fetch failed; user can retry from dashboard
    }
    toast.success('Welcome back!');

    // New users with no profile data → onboarding wizard
    const from = (location.state as { from?: { pathname?: string } })?.from?.pathname;
    if (!from) {
      try {
        const profile = await CareerProfileApiService.getProfile();
        if (!profile?.wizardCompleted && !profile?.fullName) {
          navigate('/profile/setup', { replace: true });
          return;
        }
      } catch {
        // Profile fetch failed; proceed to dashboard normally
      }
    }
    navigate(from ?? '/dashboard', { replace: true });
  };

  const form = useForm({
    defaultValues: { email: '', password: '', rememberMe: false },
    onSubmit: async ({ value }) => {
      try {
        const result = await loginMutation.mutateAsync({
          email: value.email.trim(),
          password: value.password,
          rememberMe: value.rememberMe,
        });

        if ('requiresTwoFactor' in result) {
          setChallenge({ tempToken: result.tempToken, availableMethods: result.availableMethods });
          return;
        }

        await afterLoginSuccess(result.accessToken, result.expiresIn, result.refreshExpiresIn);
      } catch (err: any) {
        const code = err?.code ?? err?.response?.data?.error?.code;
        if (code === 'ACCOUNT_ARCHIVED' || code === 'ACCOUNT_PENDING_DELETION') {
          loginMutation.reset();
          setRestoreError(null);
          setDeactivated({
            email: value.email.trim(),
            password: value.password,
            rememberMe: value.rememberMe,
            deletionScheduledFor:
              apiErrorDetails<{ deletionScheduledFor: string | null }>(err)?.deletionScheduledFor ??
              null,
          });
          return;
        }
        if (code === 'EMAIL_NOT_VERIFIED') {
          loginMutation.reset();
          try {
            await resendOtpMutation.mutateAsync({ email: value.email.trim() });
          } catch {
            /* ignore */
          }
          navigate('/activate', { state: { email: value.email.trim(), fromLogin: true } });
        }
        // All other errors shown via loginMutation.error banner
      }
    },
  });

  const handleTwoFactorSubmit = async () => {
    if (!challenge) return;
    if (useBackupCode) {
      if (!backupCode.trim()) {
        setOtpError('Enter a backup code to continue.');
        return;
      }
    } else if (otpDigits.join('').length !== 6) {
      setOtpError('Please enter all 6 digits to continue.');
      return;
    }

    try {
      const tokens = await completeTwoFactorMutation.mutateAsync(
        useBackupCode
          ? { tempToken: challenge.tempToken, backupCode: backupCode.trim() }
          : { tempToken: challenge.tempToken, method, code: otpDigits.join('') }
      );
      await afterLoginSuccess(tokens.accessToken, tokens.expiresIn, tokens.refreshExpiresIn);
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || err?.message || 'Verification failed.';
      setOtpError(message);
      setOtpDigits(['', '', '', '', '', '']);
    }
  };

  const restoreAccount = async () => {
    if (!deactivated) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      await AccountApiService.restore(deactivated.email, deactivated.password);
      const { email, password, rememberMe } = deactivated;
      setDeactivated(null);
      toast.success('Your account is restored. Finishing sign-in…');
      // Continue the normal sign-in (including the verification code step).
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      if ('requiresTwoFactor' in result) {
        setChallenge({ tempToken: result.tempToken, availableMethods: result.availableMethods });
      } else {
        await afterLoginSuccess(result.accessToken, result.expiresIn, result.refreshExpiresIn);
      }
    } catch (err) {
      setRestoreError(apiErrorMessage(err, 'We could not restore the account. Try again.'));
    } finally {
      setRestoring(false);
    }
  };

  if (deactivated) {
    const pending = deactivated.deletionScheduledFor;
    const when = pending
      ? new Date(pending).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null;
    return (
      <AuthShell>
        <div className="head">
          <h1>{pending ? 'Your account is scheduled for deletion' : 'Your account is archived'}</h1>
          <p>
            {pending
              ? `Your data will be permanently erased on ${when}. Restore the account to keep everything and sign in.`
              : 'Your profile is hidden and nothing has been deleted. Restore it to pick up where you left off.'}
          </p>
        </div>
        {restoreError && (
          <div className="auth-error-banner" role="alert">
            {restoreError}
          </div>
        )}
        <button
          type="button"
          className="btn-auth-primary auth-cta"
          onClick={restoreAccount}
          disabled={restoring}
        >
          {restoring ? 'Restoring…' : 'Restore my account'}
        </button>
        <button
          type="button"
          className="btn-auth-ghost"
          style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}
          onClick={() => setDeactivated(null)}
        >
          <ArrowLeft size={14} />
          Not now
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="head">
        <h1>{challenge ? 'Almost there' : 'Welcome back'}</h1>
        <p>
          {challenge
            ? stage === 'method'
              ? 'Choose how you want to verify it’s you.'
              : useBackupCode
                ? 'Enter one of your unused backup codes.'
                : method === 'EMAIL_OTP'
                  ? 'Enter the 6-digit code we emailed you.'
                  : 'Enter the 6-digit code from your authenticator app.'
            : 'Sign in to your Welynk workspace.'}
        </p>
      </div>

      {ssoError && !challenge && !loginMutation.error && (
        <div className="auth-error-banner" role="alert">
          {ssoError}
        </div>
      )}

      {loginMutation.error && !challenge && (
        <div className="auth-error-banner" role="alert">
          {(loginMutation.error as any)?.response?.data?.error?.message ||
            (loginMutation.error as any)?.message ||
            'Login failed. Please check your credentials.'}
        </div>
      )}

      {!challenge && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          noValidate
        >
          <form.Field
            name="email"
            validators={{
              onBlur: ({ value }) => {
                const r = loginSchema.shape.email.safeParse(value);
                return r.success ? undefined : r.error.issues[0]?.message;
              },
              onSubmit: ({ value }) => {
                const r = loginSchema.shape.email.safeParse(value);
                return r.success ? undefined : r.error.issues[0]?.message;
              },
            }}
          >
            {(field) => (
              <FloatField
                id="login-email"
                label="Email"
                type="email"
                icon="mail"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                autoComplete="email"
                error={field.state.meta.errors[0] as string | undefined}
              />
            )}
          </form.Field>

          <div style={{ marginTop: 16 }}>
            <form.Field
              name="password"
              validators={{
                onBlur: ({ value }) => {
                  const r = loginSchema.shape.password.safeParse(value);
                  return r.success ? undefined : r.error.issues[0]?.message;
                },
                onSubmit: ({ value }) => {
                  const r = loginSchema.shape.password.safeParse(value);
                  return r.success ? undefined : r.error.issues[0]?.message;
                },
              }}
            >
              {(field) => (
                <FloatField
                  id="login-password"
                  label="Password"
                  type="password"
                  icon="lock"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  autoComplete="current-password"
                  error={field.state.meta.errors[0] as string | undefined}
                />
              )}
            </form.Field>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 20,
            }}
          >
            <form.Field name="rememberMe">
              {(field) => (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: 'var(--ink-2)',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    style={{
                      width: 15,
                      height: 15,
                      accentColor: 'var(--teal)',
                      cursor: 'pointer',
                    }}
                  />
                  Remember me
                </label>
              )}
            </form.Field>
            <Link
              to="/forgot-password"
              style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 500 }}
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="btn-auth-primary auth-cta"
            style={{ marginBottom: 0 }}
          >
            {loginMutation.isPending ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      )}

      {challenge && stage === 'method' && (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            <MethodCard
              icon={<MailIcon size={18} />}
              title="Email code"
              description="We'll send a 6-digit code to your email"
              onClick={selectEmailMethod}
              busy={requestTwoFactorOtpMutation.isPending}
            />
            <MethodCard
              icon={<ShieldCheck size={18} />}
              title="Authenticator app"
              description={
                totpEnabled
                  ? 'Use the 6-digit code from your authenticator app'
                  : 'Not set up — enable it from your account settings'
              }
              onClick={selectTotpMethod}
              disabled={!totpEnabled}
            />
            <MethodCard
              icon={<Smartphone size={18} />}
              title="Mobile OTP"
              description="Get a code by SMS"
              badge="Coming soon"
              onClick={() => toast.info('Mobile OTP is coming soon.')}
              muted
            />
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {totpEnabled && (
              <button type="button" className="btn-auth-ghost" onClick={selectBackupCode}>
                <KeyRound size={14} />
                Use a backup code instead
              </button>
            )}
            <button
              type="button"
              className="btn-auth-ghost"
              onClick={() => {
                setChallenge(null);
                loginMutation.reset();
              }}
            >
              <ArrowLeft size={14} />
              Use a different account
            </button>
          </div>
        </div>
      )}

      {challenge && stage === 'code' && (
        <div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleTwoFactorSubmit();
            }}
          >
            {useBackupCode ? (
              <div style={{ marginBottom: 20 }}>
                <FloatField
                  id="login-backup-code"
                  label="Backup code"
                  icon="lock"
                  value={backupCode}
                  onChange={(e) => {
                    if (otpError) setOtpError(null);
                    setBackupCode(e.target.value);
                  }}
                  error={otpError ?? undefined}
                />
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <OtpDigitInput
                  value={otpDigits}
                  onChange={(next) => {
                    if (otpError) setOtpError(null);
                    setOtpDigits(next);
                  }}
                  error={otpError}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={completeTwoFactorMutation.isPending}
              className="btn-auth-primary auth-cta"
              style={{ marginBottom: 0 }}
            >
              {completeTwoFactorMutation.isPending ? 'Verifying…' : 'Verify and Continue'}
            </button>
          </form>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              marginTop: 14,
            }}
          >
            {!useBackupCode && method === 'EMAIL_OTP' && (
              <button
                type="button"
                className="btn-resend-code"
                disabled={requestTwoFactorOtpMutation.isPending || resendCooldown > 0}
                onClick={selectEmailMethod}
              >
                <MailIcon size={14} />
                {requestTwoFactorOtpMutation.isPending
                  ? 'Sending…'
                  : resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : 'Resend code'}
              </button>
            )}
            <button
              type="button"
              className="btn-auth-ghost"
              onClick={() => {
                resetCodeEntry();
                setUseBackupCode(false);
                setStage('method');
              }}
            >
              <ArrowLeft size={14} />
              Try another method
            </button>
          </div>
        </div>
      )}

      {!challenge && (
        <>
          <SsoButtons
            collegeSlug={collegeSlug}
            returnTo={(location.state as { from?: { pathname?: string } })?.from?.pathname}
          />

          <div className="auth-foot">
            New to Welynk? <Link to="/register">Create an account</Link>
          </div>
        </>
      )}
    </AuthShell>
  );
};
